import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, ArrowRight, Share2 } from "lucide-react";
import DOMPurify from "dompurify";
import Reveal from "../components/Reveal";
import { resolveMediaUrl, responsiveSrcSet } from "../lib/media";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Allow only safe semantic tags + Tiptap output. Strip <script>, on* handlers, javascript:, etc.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "hr", "strong", "em", "u", "s", "code", "pre", "blockquote",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "ul", "ol", "li",
    "a", "img", "figure", "figcaption",
    "table", "thead", "tbody", "tr", "th", "td",
    "span", "div",
  ],
  ALLOWED_ATTR: ["href", "src", "alt", "title", "target", "rel", "class"],
  ALLOW_DATA_ATTR: false,
};

const sanitize = (html) => DOMPurify.sanitize(html || "", SANITIZE_CONFIG);

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const readingTime = (html) => {
  const text = (html || "").replace(/<[^>]+>/g, " ").trim();
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

const ShareButtons = ({ title, url }) => {
  const enc = encodeURIComponent;
  const links = [
    {
      label: "WhatsApp",
      href: `https://api.whatsapp.com/send?text=${enc(`${title} — ${url}`)}`,
      bg: "bg-[#25D366]",
    },
    {
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
      bg: "bg-[#1877F2]",
    },
    {
      label: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
      bg: "bg-[#0A66C2]",
    },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Share2 size={14} className="text-[#8b8b8f]" />
      <span className="mr-2 font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">
        Share
      </span>
      {links.map((l) => (
        <a
          key={l.label}
          href={l.href}
          target="_blank"
          rel="noreferrer noopener"
          data-testid={`share-${l.label.toLowerCase()}`}
          onClick={() =>
            trackEvent(
              l.label === "WhatsApp" ? "whatsapp_click" : "share_click",
              { source: "blog_post", channel: l.label.toLowerCase(), title }
            )
          }
          className={`inline-flex items-center gap-1.5 rounded-full ${l.bg} px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition-all hover:opacity-90`}
        >
          {l.label}
        </a>
      ))}
    </div>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    let cancelled = false;
    const fetchPost = async () => {
      try {
        const r = await axios.get(`${API}/blogs/${slug}`);
        if (cancelled || !mounted) return;
        setPost(r.data);
        setNotFound(false);
        const res = await axios.get(`${API}/blogs?limit=50`);
        if (cancelled || !mounted) return;
        const all = Array.isArray(res.data) ? res.data : [];
        const sameCat = all.filter(
          (b) => b.category === r.data.category && b.id !== r.data.id
        );
        const others = all.filter((b) => b.id !== r.data.id);
        setRelated((sameCat.length ? sameCat : others).slice(0, 3));
      } catch {
        if (!cancelled && mounted) {
          setPost(null);
          setRelated([]);
          setNotFound(true);
        }
      }
    };
    fetchPost();
    return () => {
      mounted = false;
      cancelled = true;
    };
  }, [slug]);

  // Update document title for basic SEO
  useEffect(() => {
    if (post) {
      const seo = post.seo_title || post.title;
      document.title = `${seo} · Baheti Steel Traders`;
    }
    return () => {
      document.title = "Baheti Steel Traders";
    };
  }, [post]);

  // Inject BlogPosting JSON-LD so search engines & AI assistants understand the article
  useEffect(() => {
    if (!post) return undefined;
    const cover = post.cover_image_url
      ? resolveMediaUrl(post.cover_image_url)
      : "https://baheti.co/apple-touch-icon.png";
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.meta_description || post.excerpt || "",
      "image": cover,
      "datePublished": post.published_at || post.created_at,
      "dateModified": post.updated_at || post.published_at || post.created_at,
      "articleSection": post.category,
      "inLanguage": "en-IN",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://baheti.co/blog/${post.slug}`,
      },
      "author": { "@type": "Organization", "@id": "https://baheti.co/#org", "name": "Baheti Steel Traders" },
      "publisher": { "@id": "https://baheti.co/#org" },
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.dataset.blog = post.id;
    el.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(el);
    return () => {
      el.remove();
    };
  }, [post]);

  const url = useMemo(() => {
    if (typeof window === "undefined") return "";
    return window.location.href;
  }, []);

  if (notFound) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-[#f7f7f5] py-32">
        <div className="text-center">
          <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
            / 404
          </div>
          <h2 className="mt-4 font-display text-5xl uppercase tracking-tighter text-[#1c1c1e]">
            Article not found.
          </h2>
          <Link
            to="/blog"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1c1c1e] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white hover:bg-[#e85a1c]"
          >
            <ArrowLeft size={13} /> Back to the journal
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-[#f7f7f5]">
        <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#8b8b8f]">
          Loading article…
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page" className="grain bg-[#f7f7f5]">
      {/* HERO with cover */}
      <section className="border-b border-black/5 bg-white">
        <div className="mx-auto max-w-[1100px] px-6 pt-16 lg:px-10">
          <Link
            to="/blog"
            data-testid="blog-back"
            className="inline-flex items-center gap-2 font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f] transition-colors hover:text-[#e85a1c]"
          >
            <ArrowLeft size={11} /> Back to the journal
          </Link>
          <Reveal className="mt-8">
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / {post.category}
            </div>
            <h1
              className="mt-5 font-display text-4xl uppercase leading-[0.95] tracking-tighter text-[#1c1c1e] md:text-6xl"
              data-testid="blog-post-title"
            >
              {post.title}
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono-industrial text-[11px] uppercase tracking-[0.25em] text-[#8b8b8f]">
              <span className="inline-flex items-center gap-2">
                <Calendar size={12} />
                {formatDate(post.published_at || post.created_at)}
              </span>
              <span className="inline-flex items-center gap-2">
                <Clock size={12} />
                {readingTime(post.content)} min read
              </span>
            </div>
          </Reveal>
          {post.cover_image_url && (
            <Reveal delay={120} className="mt-12">
              <img
                src={resolveMediaUrl(post.cover_image_url)}
                srcSet={responsiveSrcSet(post.cover_image_url, [480, 800, 1200, 1600])}
                sizes="(min-width: 1024px) 1040px, 92vw"
                alt={post.title}
                loading="lazy"
                decoding="async"
                data-testid="blog-post-cover"
                className="aspect-[16/9] w-full rounded-3xl object-cover shadow-lux"
              />
            </Reveal>
          )}
        </div>
      </section>

      {/* BODY */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-[760px] px-6 lg:px-10">
          {post.excerpt && (
            <Reveal className="mb-10 border-l-4 border-[#e85a1c] pl-6">
              <p className="font-display-med text-lg italic leading-[1.55] text-[#1c1c1e] md:text-2xl">
                {post.excerpt}
              </p>
            </Reveal>
          )}
          <Reveal delay={80}>
            <article
              data-testid="blog-post-content"
              className="blog-prose"
              dangerouslySetInnerHTML={{ __html: sanitize(post.content) }}
            />
          </Reveal>

          <Reveal delay={140} className="mt-12 border-t border-black/5 pt-8">
            <ShareButtons title={post.title} url={url} />
          </Reveal>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="border-t border-black/5 bg-[#f7f7f5] py-20">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <Reveal>
              <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
                / Related reading
              </div>
              <h3 className="mt-3 font-display text-3xl uppercase tracking-tighter text-[#1c1c1e] md:text-5xl">
                More from the journal.
              </h3>
            </Reveal>
            <div className="mt-12 grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((b, i) => (
                <Reveal key={b.id} delay={i * 80}>
                  <Link
                    to={`/blog/${b.slug}`}
                    data-testid={`related-card-${b.slug}`}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-black/5 bg-white shadow-lux transition-all duration-300 hover:-translate-y-1 hover:shadow-lux-hover"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-[#efefe9]">
                      {b.cover_image_url && (
                        <img
                          src={resolveMediaUrl(b.cover_image_url)}
                          srcSet={responsiveSrcSet(b.cover_image_url, [320, 480, 640, 800])}
                          sizes="(min-width: 1024px) 32vw, (min-width: 640px) 48vw, 92vw"
                          alt={b.title}
                          loading="lazy"
                          decoding="async"
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <div className="font-mono-industrial text-[9px] uppercase tracking-[0.3em] text-[#e85a1c]">
                        {b.category}
                      </div>
                      <h4 className="mt-3 line-clamp-2 font-display-med text-lg uppercase leading-tight tracking-tight text-[#1c1c1e]">
                        {b.title}
                      </h4>
                      <span className="mt-auto inline-flex items-center gap-1.5 pt-5 font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
                        Read
                        <ArrowRight
                          size={11}
                          className="transition-transform duration-300 group-hover:translate-x-1"
                        />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
